import { motion } from "framer-motion";
import { X, Check } from "lucide-react";

const comparisons = [
    {
        problem: "Paper registers & manual roll calls for attendance",
        solution: "One-click digital attendance with instant parent SMS alerts",
    },
    {
        problem: "Exam marks calculated manually on spreadsheets",
        solution: "Automated MCQ & descriptive grading with instant report cards",
    },
    {
        problem: "Fee collection via cash with no tracking or receipts",
        solution: "Online payment gateways, automated invoices & fee reminders",
    },
    {
        problem: "Parents have no idea what's happening in school",
        solution: "Real-time notifications & parent app with full visibility",
    },
    {
        problem: "Student records lost or scattered across files",
        solution: "Centralized cloud database with instant search & access",
    },
    {
        problem: "Timetable scheduling takes days of manual effort",
        solution: "Drag-and-drop timetable builder done in minutes",
    },
    {
        problem: "No way to communicate quickly with all teachers",
        solution: "In-app group chat & broadcast push notifications",
    },
    {
        problem: "Library & inventory tracked in ledger books",
        solution: "Digital asset tracking with barcode scan & auto-alerts",
    },
];

const ComparisonSection = () => (
    <section className="py-24 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-4 lg:px-8 max-w-6xl">
            <div className="text-center mb-16 max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-5xl font-heading font-extrabold text-slate-800 dark:text-slate-100 mb-6 tracking-tight">
                    Why Schools Switch to eSchool
                </h2>
                <p className="text-lg text-slate-500 dark:text-slate-400 font-medium leading-relaxed">
                    Still running your school the old way? Here's why hundreds of schools have made the switch.
                </p>
            </div>

            {/* Column Headers */}
            <div className="grid grid-cols-2 gap-4 mb-4 max-w-5xl mx-auto px-2">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-red-100 dark:bg-red-900/40 flex items-center justify-center shrink-0">
                        <X className="w-4 h-4 text-red-500" />
                    </div>
                    <span className="font-bold text-slate-600 dark:text-slate-400 text-sm uppercase tracking-wide">Without eSchool</span>
                </div>
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/40 flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="font-bold text-slate-600 dark:text-slate-400 text-sm uppercase tracking-wide">With eSchool</span>
                </div>
            </div>

            {/* Comparison Rows */}
            <div className="max-w-5xl mx-auto space-y-3">
                {comparisons.map((item, i) => (
                    <motion.div
                        key={i}
                        className="grid grid-cols-2 gap-4"
                    >
                        {/* Problem */}
                        <div className="flex items-start gap-3 p-4 rounded-2xl bg-red-50 dark:bg-red-950/30 border border-red-100 dark:border-red-900/30">
                            <div className="w-6 h-6 rounded-full bg-red-100 dark:bg-red-900/60 flex items-center justify-center shrink-0 mt-0.5">
                                <X className="w-3 h-3 text-red-500" />
                            </div>
                            <p className="text-sm text-slate-700 dark:text-slate-300 font-medium leading-relaxed">{item.problem}</p>
                        </div>

                        {/* Solution */}
                        <div className="flex items-start gap-3 p-4 rounded-2xl bg-green-50 dark:bg-green-950/30 border border-green-100 dark:border-green-900/30">
                            <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/60 flex items-center justify-center shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-green-600" />
                            </div>
                            <p className="text-sm text-slate-700 dark:text-slate-300 font-semibold leading-relaxed">{item.solution}</p>
                        </div>
                    </motion.div>
                ))}
            </div>

            {/* Gradient badge at bottom */}
            <motion.div
                className="mt-12 text-center"
            >
                <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 text-white text-sm font-bold shadow-lg shadow-blue-500/20">
                    <Check className="w-4 h-4" />
                    eSchool covers all of this — and much more
                </div>
            </motion.div>
        </div>
    </section>
);

export default ComparisonSection;
